/**
 * =============================================================
 * AADHAV ACADEMY – STUDENT PERFORMANCE & TOURNAMENT GAMES SLICE
 * =============================================================
 */

import { createSlice } from "@reduxjs/toolkit";
import {
  fetchStudentPerformance,
  fetchChessOpeningsThunk,
  createTournamentPerformanceThunk,
  updateTournamentPerformanceThunk,
  deleteTournamentPerformanceThunk,
  deleteTournamentGameThunk,
} from "./studentPerformance.thunk";

const initialState = {
  performanceData: [], // Array of tournaments, each with a 'games' array
  singleTournament: null, // For single record view/detail if needed
  openingsList: [], // Master list of chess openings fetched from database
  summary: { total_tournaments: 0, total_wins: 0, total_points: 0 },
  meta: {
    total: 0,
    total_pages: 1,
    page: 1,
    limit: 10,
  },
  loading: false,
  openingsLoading: false,
  error: null,
};

const studentPerformanceSlice = createSlice({
  name: "studentPerformance",
  initialState,
  reducers: {
    clearPerformanceError: (state) => {
      state.error = null;
    },
    clearSingleTournament: (state) => {
      state.singleTournament = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ================= FETCH PERFORMANCE ================= */
      .addCase(fetchStudentPerformance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudentPerformance.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;

        // Check if fetching a single tournament performance record by ID
        if (payload && !Array.isArray(payload) && payload.data && !Array.isArray(payload.data) && payload.data.id) {
          state.singleTournament = payload.data;
          return;
        }

        // Handle normal list or normalized response payload
        let performanceList = [];
        if (Array.isArray(payload)) {
          performanceList = payload;
        } else if (Array.isArray(payload?.data)) {
          performanceList = payload.data;
        }

        state.performanceData = performanceList;

        // Store Summary & Meta from API safely
        state.summary = payload?.summary || {
          total_tournaments: performanceList.length,
          total_wins: 0,
          total_points: 0,
        };

        state.meta = payload?.meta || {
          total: performanceList.length,
          total_pages: 1,
          page: 1,
          limit: 10,
        };
      })
      .addCase(fetchStudentPerformance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch performance data";
      })

      /* ================= FETCH CHESS OPENINGS ================= */
      .addCase(fetchChessOpeningsThunk.pending, (state) => {
        state.openingsLoading = true;
      })
      .addCase(fetchChessOpeningsThunk.fulfilled, (state, action) => {
        state.openingsLoading = false;
        state.openingsList = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchChessOpeningsThunk.rejected, (state) => {
        state.openingsLoading = false;
        state.openingsList = [];
      })

      /* ================= CREATE TOURNAMENT ================= */
      .addCase(createTournamentPerformanceThunk.fulfilled, (state, action) => {
        state.loading = false;
        const newRecord = action.payload?.data || action.payload?.requestPayload;
        if (newRecord) {
          state.performanceData.unshift(newRecord);
        }
      })
      .addCase(createTournamentPerformanceThunk.rejected, (state, action) => {
        state.error = action.payload;
      })

      /* ================= UPDATE TOURNAMENT ================= */
      .addCase(updateTournamentPerformanceThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { id, requestPayload } = action.payload || {};
        if (!id) return;

        const index = state.performanceData.findIndex((t) => Number(t.id) === Number(id));

        if (index !== -1) {
          state.performanceData[index] = {
            ...state.performanceData[index],
            ...requestPayload,
            id: id,
          };
        }

        if (state.singleTournament && Number(state.singleTournament.id) === Number(id)) {
          state.singleTournament = {
            ...state.singleTournament,
            ...requestPayload,
            id: id,
          };
        }
      })
      .addCase(updateTournamentPerformanceThunk.rejected, (state, action) => {
        state.error = action.payload;
      })

      /* ================= DELETE TOURNAMENT ================= */
      .addCase(deleteTournamentPerformanceThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { tournamentPerformanceId } = action.payload || {};
        if (!tournamentPerformanceId) return;

        state.performanceData = state.performanceData.filter(
          (t) => Number(t.id) !== Number(tournamentPerformanceId)
        );

        if (state.singleTournament && Number(state.singleTournament.id) === Number(tournamentPerformanceId)) {
          state.singleTournament = null;
        }
      })
      .addCase(deleteTournamentPerformanceThunk.rejected, (state, action) => {
        state.error = action.payload;
      })

      /* ================= DELETE GAME ================= */
      .addCase(deleteTournamentGameThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { gameId, tournamentId } = action.payload || {};
        if (!gameId) return;

        // Update in performanceData list
        if (tournamentId) {
          const tournament = state.performanceData.find((t) => Number(t.id) === Number(tournamentId));
          if (tournament && Array.isArray(tournament.games)) {
            tournament.games = tournament.games.filter((g) => Number(g.id) !== Number(gameId));
          }
        } else {
          // Fallback if tournamentId wasn't passed, search across all records
          state.performanceData.forEach((t) => {
            if (Array.isArray(t.games)) {
              t.games = t.games.filter((g) => Number(g.id) !== Number(gameId));
            }
          });
        }

        // Update in singleTournament view if active
        if (state.singleTournament && Array.isArray(state.singleTournament.games)) {
          if (!tournamentId || Number(state.singleTournament.id) === Number(tournamentId)) {
            state.singleTournament.games = state.singleTournament.games.filter(
              (g) => Number(g.id) !== Number(gameId)
            );
          }
        }
      })
      .addCase(deleteTournamentGameThunk.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearPerformanceError, clearSingleTournament } = studentPerformanceSlice.actions;
export default studentPerformanceSlice.reducer;